/**
 * @module hotspotSvgUtils
 * Utility helpers for converting Scene7 hotspot background SVGs into inline markup while
 * applying lightweight sanitisation and consistent metadata handling.
 *
 * The primary entry point is {@link inlineSvgFromBackground}. Given a hotspot overlay element,
 * the helper inspects its `background-image`, fetches the referenced SVG, sanitises it, injects
 * the markup directly into the DOM, and copies across author-defined metadata (e.g. link targets).
 *
 * Future maintainers should favour this helper over ad-hoc DOM manipulation so that sanitisation
 * and security controls remain centralised.
 */

/** Describes the attribute applied to hotspots when a custom SVG icon is inlined. */
interface MarkerAttribute {
  /** Attribute name, e.g. `data-custom-icon`. */
  name: string;
  /** Optional attribute value; defaults to the string "true". */
  value?: string;
}

/** Maps metadata keys (e.g. `href`) to DOM attribute names (e.g. `data-href`). */
interface MetadataAttributeMap {
  [key: string]: string;
}

/** Arbitrary metadata that should be reflected on the hotspot element as attributes. */
interface HotspotMetadata {
  [key: string]: string | null | undefined;
}

/** Diagnostic context forwarded to error callbacks. */
interface ErrorContext {
  element: Element;
  svgUrl: string;
}

/**
 * Fine-grained controls for sanitisation, security and metadata propagation.
 *
 * @property requestInit Optional overrides forwarded to `fetch`. Defaults to
 * `{ credentials: "same-origin" }`, ensuring existing Scene7-hosted assets continue to work with
 * authenticated deployments. Adjust this if SVGs are served by a different domain or require
 * additional headers.
 * @property allowedOrigins Whitelist of origins allowed to supply SVG markup. Accepts the special
 * value "self" which resolves to `window.location.origin` at runtime.
 */
export interface HotspotSvgOptions {
  markerAttribute?: MarkerAttribute;
  allowedOrigins?: string[] | null;
  requestInit?: RequestInit;
  sanitizeSvg?: boolean;
  removeScripts?: boolean;
  stripEventAttributes?: boolean;
  logErrors?: boolean;
  metadataAttributeMap?: MetadataAttributeMap;
  onError?: (error: unknown, context: ErrorContext) => void;
}

/** Configuration accepted by {@link inlineSvgFromBackground}. */
export interface InlineSvgConfig extends HotspotSvgOptions {
  /** Metadata to copy onto the hotspot element, such as `href` or `title`. */
  metadata?: HotspotMetadata | null;
}

/** Internal representation of merged/defaulted options. */
interface ResolvedOptions {
  markerAttribute: MarkerAttribute;
  allowedOrigins: string[] | null;
  requestInit: RequestInit;
  sanitizeSvg: boolean;
  removeScripts: boolean;
  stripEventAttributes: boolean;
  logErrors: boolean;
  metadataAttributeMap: MetadataAttributeMap;
  onError?: (error: unknown, context: ErrorContext) => void;
}

const DEFAULT_OPTIONS: Omit<ResolvedOptions, "onError"> = {
  markerAttribute: { name: "data-custom-icon", value: "true" },
  allowedOrigins: null,
  requestInit: { credentials: "same-origin" },
  sanitizeSvg: true,
  removeScripts: true,
  stripEventAttributes: true,
  logErrors: true,
  metadataAttributeMap: { href: "data-href", title: "data-title" },
};

/**
 * Merge consumer-provided config with default safeguards.
 */
function buildOptions(config: InlineSvgConfig = {}): ResolvedOptions {
  const allowedOrigins = normalizeAllowedOrigins(
    Object.prototype.hasOwnProperty.call(config, "allowedOrigins")
      ? (config.allowedOrigins ?? null)
      : DEFAULT_OPTIONS.allowedOrigins,
  );

  return {
    markerAttribute: {
      ...DEFAULT_OPTIONS.markerAttribute,
      ...(config.markerAttribute ?? {}),
    },
    allowedOrigins,
    requestInit: {
      ...DEFAULT_OPTIONS.requestInit,
      ...(config.requestInit ?? {}),
    },
    sanitizeSvg: config.sanitizeSvg ?? DEFAULT_OPTIONS.sanitizeSvg,
    removeScripts: config.removeScripts ?? DEFAULT_OPTIONS.removeScripts,
    stripEventAttributes:
      config.stripEventAttributes ?? DEFAULT_OPTIONS.stripEventAttributes,
    logErrors: config.logErrors ?? DEFAULT_OPTIONS.logErrors,
    metadataAttributeMap: {
      ...DEFAULT_OPTIONS.metadataAttributeMap,
      ...(config.metadataAttributeMap ?? {}),
    },
    onError: config.onError,
  };
}

/**
 * Normalise an origins whitelist, supporting the special keyword "self".
 */
function normalizeAllowedOrigins(origins?: string[] | null): string[] | null {
  if (!origins || !Array.isArray(origins) || origins.length === 0) {
    return null;
  }

  const baseOrigin =
    typeof window !== "undefined" ? window.location.origin : undefined;

  const normalized = origins
    .filter(Boolean)
    .map((origin) => {
      if (!origin) {
        return null;
      }

      if (origin === "self") {
        return baseOrigin ?? null;
      }

      try {
        return new URL(origin, baseOrigin).origin;
      } catch (error) {
        if (console && console.warn) {
          console.warn(`[HotspotSvg] Ignoring invalid origin: ${origin}`);
        }
        return null;
      }
    })
    .filter((value): value is string => Boolean(value));

  return normalized.length ? Array.from(new Set(normalized)) : null;
}

/**
 * Extract the first URL reference from a CSS `background-image` declaration.
 */
export function extractSvgUrl(backgroundValue: string | null): string | null {
  if (!backgroundValue || backgroundValue === "none") {
    return null;
  }

  const matches = backgroundValue.match(/url\((['"]?)(.+?)\1\)/i);
  return matches && matches[2] ? matches[2].trim() : null;
}

/** Simple heuristic to check that the referenced resource appears to be an SVG. */
function isSvgResource(url: string | null): url is string {
  if (!url) {
    return false;
  }

  return /\.svg(\?|#|$)/i.test(url);
}

/** Guard against cross-domain SVG injection unless explicitly whitelisted. */
function isAllowedOrigin(
  url: string,
  allowedOrigins: string[] | null,
): boolean {
  if (!allowedOrigins) {
    return true;
  }

  try {
    const resourceOrigin = new URL(
      url,
      typeof window !== "undefined" ? window.location.href : undefined,
    ).origin;
    return allowedOrigins.includes(resourceOrigin);
  } catch (error) {
    return false;
  }
}

/** Fetch the remote SVG markup, surfacing informative errors when possible. */
async function loadSvg(url: string, requestInit: RequestInit): Promise<string> {
  const response = await fetch(url, requestInit);

  if (!response.ok) {
    throw new Error(
      `[HotspotSvg] Request failed with status ${response.status} (${response.statusText})`,
    );
  }

  const contentType = response.headers?.get?.("Content-Type") ?? null;

  if (contentType && contentType.indexOf("image/svg") === -1) {
    console.warn(
      `[HotspotSvg] Retrieved resource does not advertise an SVG content type (${contentType}).`,
    );
  }

  return response.text();
}

/**
 * Remove script nodes, inline event handlers, and suspicious styles from SVG markup.
 */
function sanitizeSvgMarkup(
  svgMarkup: string,
  options: ResolvedOptions,
): string {
  const parser = new DOMParser();
  const documentResult = parser.parseFromString(svgMarkup, "image/svg+xml");
  const parserError = documentResult.querySelector("parsererror");

  if (parserError) {
    throw new Error("[HotspotSvg] Invalid SVG markup received.");
  }

  const svgElement = documentResult.documentElement;

  if (!svgElement || svgElement.nodeName.toLowerCase() !== "svg") {
    throw new Error(
      "[HotspotSvg] Provided markup is missing an <svg> root element.",
    );
  }

  if (options.removeScripts) {
    const scriptNodes = svgElement.querySelectorAll("script");
    scriptNodes.forEach((scriptNode) => {
      const parentNode = scriptNode.parentNode;
      if (parentNode) {
        parentNode.removeChild(scriptNode);
      }
    });
  }

  if (options.stripEventAttributes) {
    const nodes = [svgElement, ...Array.from(svgElement.querySelectorAll("*"))];

    nodes.forEach((node) => {
      Array.from(node.attributes).forEach((attribute) => {
        const attributeName = attribute.name.toLowerCase();
        const attributeValue = (attribute.value || "").trim();

        if (attributeName.startsWith("on")) {
          node.removeAttribute(attribute.name);
          return;
        }

        if (/^javascript:/i.test(attributeValue)) {
          node.removeAttribute(attribute.name);
          return;
        }

        if (attributeName === "style") {
          const sanitizedStyle = sanitizeInlineStyle(attribute.value);
          if (sanitizedStyle === null) {
            node.removeAttribute(attribute.name);
          } else if (sanitizedStyle !== attribute.value) {
            node.setAttribute(attribute.name, sanitizedStyle);
          }
        }
      });
    });
  }

  return new XMLSerializer().serializeToString(svgElement);
}

/** Remove JavaScript execution vectors from inline styles. */
function sanitizeInlineStyle(styleValue: string): string | null {
  if (!styleValue) {
    return "";
  }

  const unsafePattern = /expression\s*\(|url\s*\(\s*['"]?\s*javascript:/i;
  return unsafePattern.test(styleValue) ? null : styleValue;
}

/** Apply the configured marker attribute to signal a customised hotspot icon. */
function applyMarkerAttribute(
  element: Element,
  markerAttribute: MarkerAttribute,
): void {
  if (!markerAttribute || !markerAttribute.name) {
    return;
  }

  const attributeValue =
    typeof markerAttribute.value === "undefined"
      ? "true"
      : markerAttribute.value;

  element.setAttribute(markerAttribute.name, attributeValue);
}

/** Copy metadata values onto the hotspot element using the configured attribute map. */
function applyMetadataAttributes(
  element: Element,
  metadata: HotspotMetadata | null,
  attributeMap: MetadataAttributeMap,
): void {
  if (!metadata || typeof metadata !== "object") {
    return;
  }

  Object.keys(metadata).forEach((key) => {
    const value = metadata[key];
    if (value === undefined || value === null) {
      return;
    }

    const mappedAttribute = attributeMap[key] || key;
    try {
      element.setAttribute(mappedAttribute, String(value));
    } catch (error) {
      if (console && console.warn) {
        console.warn(
          `[HotspotSvg] Failed to set attribute ${mappedAttribute} on hotspot element.`,
        );
      }
    }
  });
}

/**
 * Extracts the background-image CSS property value from an element.
 * @param element - The DOM element to extract the background image from
 * @returns The background-image CSS property value as a string
 */
function extractBackgroundImage(element: Element): string {
  const computedStyle = window.getComputedStyle(element);
  const backgroundValue = computedStyle
    ? computedStyle.getPropertyValue("background-image")
    : "";

  return backgroundValue;
}

const BACKGROUND_IMAGE_MAX_WAIT_MS = 5000;
const BACKGROUND_IMAGE_POLL_INTERVAL_MS = 80;

/**
 * Waits for background image styles to be applied and ready on an element.
 * Polls the element's computed styles until background image is loaded or timeout is reached.
 * @param element - The DOM element to monitor for background image readiness
 * @returns Promise that resolves when background image styles are ready or rejects on timeout
 */
function waitForBackgroundImageStylesReady(
  element: Element,
  maxWaitMs = BACKGROUND_IMAGE_MAX_WAIT_MS,
  pollIntervalMs = BACKGROUND_IMAGE_POLL_INTERVAL_MS,
): Promise<string> {
  return new Promise((resolve) => {
    const startTime = Date.now();
    const check = () => {
      const styles = extractBackgroundImage(element);

      if (
        (styles && styles !== "none") ||
        Date.now() - startTime >= maxWaitMs
      ) {
        resolve(styles);
        return;
      }
      window.setTimeout(check, pollIntervalMs);
    };

    check();
  });
}

/**
 * Replace an SVG background-image with inline SVG markup on a hotspot overlay element.
 *
 * @param element Hotspot DOM node created by the Scene7 viewer.
 * @param config Optional configuration controlling sanitisation, metadata and security behaviour.
 * @returns Resolves with `true` when the SVG was inlined, otherwise `false`.
 *
 * @example
 * ```ts
 * import { inlineSvgFromBackground } from "../utils/hotspotSvgUtils";
 *
 * const hotspot = document.querySelector(".s7mapoverlay");
 * if (hotspot) {
 *   inlineSvgFromBackground(hotspot, {
 *     metadata: { href: "https://example.com", title: "CTA" },
 *     allowedOrigins: ["self", "https://assets.scene7.com"],
 *     requestInit: { credentials: "include" },
 *   });
 * }
 * ```
 */
export async function inlineSvgFromBackground(
  element: Element,
  config: InlineSvgConfig = {},
): Promise<boolean> {
  if (!(element instanceof Element)) {
    throw new TypeError(
      "[HotspotSvg] inlineSvgFromBackground expects a DOM element instance.",
    );
  }

  if (typeof window === "undefined") {
    return false;
  }

  const options = buildOptions(config);
  const metadata = config.metadata ?? null;
  const backgroundValue = await waitForBackgroundImageStylesReady(element);

  const svgUrl = extractSvgUrl(backgroundValue);

  if (!svgUrl || !isSvgResource(svgUrl)) {
    return false;
  }

  if (!isAllowedOrigin(svgUrl, options.allowedOrigins)) {
    if (options.logErrors && console && console.warn) {
      console.warn(
        `[HotspotSvg] Blocked SVG from disallowed origin: ${svgUrl}`,
      );
    }
    return false;
  }

  applyMetadataAttributes(element, metadata, options.metadataAttributeMap);

  try {
    const svgMarkup = await loadSvg(svgUrl, options.requestInit);
    if (!svgMarkup) {
      return false;
    }

    const safeMarkup = options.sanitizeSvg
      ? sanitizeSvgMarkup(svgMarkup, options)
      : svgMarkup;

    element.innerHTML = safeMarkup;
    applyMarkerAttribute(element, options.markerAttribute);

    return true;
  } catch (error) {
    if (typeof options.onError === "function") {
      options.onError(error, { element, svgUrl });
    } else if (options.logErrors && console && console.error) {
      console.error(error);
    }
    return false;
  }
}

type HotspotGlobalNamespace = {
  inlineSvgFromBackground?: typeof inlineSvgFromBackground;
  inlineSvgReadyEventName?: string;
  [key: string]: unknown;
};

declare global {
  interface Window {
    HotspotSvg?: HotspotGlobalNamespace;
  }
}

const HOTSPOT_INLINE_SVG_READY_EVENT = "hotspot:inline-svg-ready";
const hotspotWindow = window as Window;

hotspotWindow.HotspotSvg = hotspotWindow.HotspotSvg || {};

const hotspotNamespace = hotspotWindow.HotspotSvg as HotspotGlobalNamespace;
hotspotNamespace.inlineSvgFromBackground = inlineSvgFromBackground;
hotspotNamespace.inlineSvgReadyEventName =
  hotspotNamespace.inlineSvgReadyEventName || HOTSPOT_INLINE_SVG_READY_EVENT;

const eventName = hotspotNamespace.inlineSvgReadyEventName;

if (typeof hotspotWindow.dispatchEvent === "function") {
  hotspotWindow.dispatchEvent(new CustomEvent(eventName));
}

export default inlineSvgFromBackground;
